import { useEffect, useState } from "react";
import clsx from "clsx";
import { PortalDialogProps } from "@app/services/modal.service";
import { RECHARGE_COUNTDOWN_TIME } from "@app/constants";
import { Recharge } from "@app/types";
import { formatMoneyVN } from "@app/utils/util";
import { useT } from "@app/contexts/preferences.context";
import Modal from "../modal/modal.component";
import { IcCheck, IcClock, IcX } from "../icons";
import { CopyButton, btn, btnGhost, btnLg, btnPrimary, iconBtn } from "../ui";

/**
 * Tách thông tin nhận tiền từ link_payment của đơn nạp:
 * https://img.vietqr.io/image/{bank_name}-{bank_number}-print.png?amount=..&addInfo=..&accountName=..
 */
export const parsePaymentLink = (link?: string) => {
  try {
    const url = new URL(link || "");
    const file = decodeURIComponent(url.pathname.split("/").pop() || "").replace(/-print\.png$/, "");
    const dash = file.lastIndexOf("-");

    return {
      bankName: dash > 0 ? file.slice(0, dash) : file,
      bankNumber: dash > 0 ? file.slice(dash + 1) : "",
      accountName: url.searchParams.get("accountName") || "",
    };
  } catch {
    return { bankName: "", bankNumber: "", accountName: "" };
  }
};

/** Thời gian còn lại của đơn nạp (15 phút từ lúc tạo, như modal cũ) */
export const rechargeTimeLeft = (recharge: Recharge) =>
  Math.max(0, new Date(recharge.createdAt).getTime() + RECHARGE_COUNTDOWN_TIME - Date.now());

const mmss = (ms: number) => {
  const s = Math.floor(ms / 1000);

  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

/**
 * Mã QR chuyển khoản của 1 đơn nạp (thay cho QRInfoModal cũ).
 * Đóng với { isCancel: true } khi bấm Huỷ nạp, { isCompleted: true } khi bấm Tôi đã chuyển khoản hoặc hết giờ.
 */
function RechargeQrModal({ portalDialogRef, portalData }: PortalDialogProps<{ recharge: Recharge }>) {
  const t = useT();
  const recharge = portalData?.recharge as Recharge;
  const [left, setLeft] = useState(() => (recharge ? rechargeTimeLeft(recharge) : 0));
  const [imgLoaded, setImgLoaded] = useState(false);
  const [sure, setSure] = useState(false);

  useEffect(() => {
    if (!recharge) return;

    const timer = setInterval(() => {
      const next = rechargeTimeLeft(recharge);

      setLeft(next);

      if (next <= 0) {
        clearInterval(timer);
        portalDialogRef.close({ isCompleted: true });
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (!recharge) return null;

  const bank = parsePaymentLink(recharge.link_payment);
  const percent = (left / RECHARGE_COUNTDOWN_TIME) * 100;

  const row = (label: string, value: string, copy?: string, highlight?: boolean) => (
    <div
      className={clsx(
        "flex items-center justify-between gap-2 rounded-[10px] px-3 py-2",
        highlight ? "bg-brand-soft" : "bg-surface-2"
      )}
    >
      <span className="text-[12.5px] text-muted">{t(label)}</span>
      <span className="flex min-w-0 items-center gap-0.5">
        <b className={clsx("truncate text-[13.5px] tabular-nums", highlight && "font-mono text-brand")}>{value}</b>
        {copy && <CopyButton value={copy} label={`Sao chép ${label.toLowerCase()}`} />}
      </span>
    </div>
  );

  return (
    <Modal onCancel={() => portalDialogRef.close()} buttonCancelInChildren={false}>
      <div className="flex max-h-[min(92vh,820px)] w-[min(680px,calc(100vw-32px))] flex-col text-fg">
        <div className="flex items-start justify-between gap-3 border-b border-line px-6 pb-4 pt-5">
          <div>
            <h2 className="text-[19px] font-bold">{t("Quét mã để chuyển khoản")}</h2>
            <p className="m-0 mt-0.5 text-[13px] tabular-nums text-muted">
              {formatMoneyVN(recharge.money)} VND · {bank.bankName || recharge.bank}
            </p>
          </div>
          <button type="button" className={clsx(iconBtn, "!h-8 !w-8")} aria-label={t("Đóng")} onClick={() => portalDialogRef.close()}>
            <IcX className="h-4 w-4" />
          </button>
        </div>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-6 py-5">
          <div className="flex flex-col gap-1.5">
            <span className="flex items-center gap-1.5 text-[13px] text-muted">
              <IcClock className="h-4 w-4" />
              {t("Mã QR hết hạn sau")} <b className="tabular-nums text-fg">{mmss(left)}</b>
            </span>
            <div className="h-1.5 overflow-hidden rounded bg-surface-2">
              <i className={clsx("block h-full rounded", percent < 20 ? "bg-bad" : "bg-brand")} style={{ width: `${percent}%` }} />
            </div>
          </div>

          <div className="grid grid-cols-[240px_minmax(0,1fr)] items-start gap-5 max-[760px]:grid-cols-1">
            <div className="flex flex-col items-center gap-2 rounded-xl border border-line bg-white p-3 max-[760px]:mx-auto max-[760px]:w-full max-[760px]:max-w-[240px]">
              {!imgLoaded && <div className="grid h-[220px] w-full place-items-center text-[13px] text-muted">{t("Đang tải mã QR…")}</div>}
              <img
                src={recharge.link_payment}
                alt={t("Mã QR chuyển khoản")}
                className={clsx("w-full", !imgLoaded && "hidden")}
                onLoad={() => setImgLoaded(true)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              {row("Số tiền", `${formatMoneyVN(recharge.money)} VND`, String(recharge.money))}
              {row("Nội dung CK", recharge.content, recharge.content, true)}
              <small className="px-1 text-xs font-medium text-warn">
                {t("Ghi đúng số tiền và nội dung để số dư được cộng tự động.")}
              </small>
              {bank.bankNumber && row("Số tài khoản", bank.bankNumber, bank.bankNumber)}
              {bank.accountName && row("Chủ tài khoản", bank.accountName, bank.accountName)}
              {row("Ngân hàng", bank.bankName || recharge.bank)}
            </div>
          </div>
        </div>

        <div className="flex flex-col gap-2 border-t border-line px-6 py-4">
          <div className="flex gap-2 max-[760px]:flex-col-reverse">
            <button
              type="button"
              className={clsx(btn, btnGhost, "!h-11", sure && "!border-bad !text-bad")}
              onClick={() => (sure ? portalDialogRef.close({ isCancel: true }) : setSure(true))}
            >
              {sure ? t("Bấm lần nữa để huỷ") : t("Huỷ nạp")}
            </button>
            <button type="button" className={clsx(btn, btnPrimary, btnLg, "flex-1")} onClick={() => portalDialogRef.close({ isCompleted: true })}>
              <IcCheck className="h-[18px] w-[18px]" />
              {t("Tôi đã chuyển khoản")}
            </button>
          </div>
          <p className="m-0 text-center text-xs text-faint">
            {t("Đóng cửa sổ này không huỷ lệnh nạp. Bạn có thể mở lại mã QR trong Biến động số dư.")}
          </p>
        </div>
      </div>
    </Modal>
  );
}

export default RechargeQrModal;
